import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Cookie from 'utils/cookies';
import { UNIFIED_FEED_DOMAIN_SERVICE, TOI_IMAGE_DOMAIN } from 'constants';
import makeRequest from 'utils/makeRequest';
import { formatTimeMMSS } from 'routes/games/layouts/utils';
import analyticsWrapper from 'helpers/analytics/analyticsWrapper';
import LoaderSpinner from 'components_v2/common/LoaderSpinner/LoaderSpinner';
import s from './GameSolution.scss';
import LoopALoopGame from '../LoopALoopGame/LoopALoopGame';
import SudukoGame from '../SudukoGame/SudukoGame';
import CrosswordGame from '../CrosswordGame/CrosswordGame';
import WhoIsThatGame from '../WhoIsThatGame/WhoIsThatGame';
import LocationGuesserGame from '../LocationGuesserGame/LocationGuesserGame';
import ConnectGame from '../ConnectGame/ConnectGame';

// Helper function to render time line items
const renderTimeLineItem = (label, value, formatter = null, gameType) => {
  if (!value) return null;

  const displayValue = formatter ? formatter(value) : value;

  return (
    <div className={s.timeLineItem}>
      <strong>{label} : </strong>
      <span>{displayValue}</span>
      {gameType === 'location_guesser' &&
        label === 'Best Score' && <i className={s.starIcon} />}
    </div>
  );
};

const GameSolution = ({
  buttonClicked,
  isMobile,
  gameId,
  gameType,
  gameLevel,
  gamejsonUrl,
  showLoader: externalShowLoader,
}) => {
  const formattedGameType = gameType.replace(/-/g, '_');
  const playAgainCheck = !!(
    buttonClicked &&
    (buttonClicked === 'playAgain' || buttonClicked === 'play_again')
  );
  // eslint-disable-next-line no-unused-vars
  const [gameSolutionData, setGameSolutionData] = useState(null);
  const [showLoader, setLoader] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);

  useEffect(
    () => {
      // Showing timeline after 100ms
      const timer = setTimeout(() => setShowTimeline(true), 100);
      return () => clearTimeout(timer);
    },
    [gameId, gameType],
  );

  let grxId = Cookie.get('_grx');
  let ssoId = Cookie.get('ssoid');
  grxId = grxId || '';
  ssoId = ssoId || '';

  const getSolutionData = () => {
    const tempplayagaincheck = playAgainCheck ? '&playAgain=true' : '';
    const url = `${UNIFIED_FEED_DOMAIN_SERVICE}/ufs-utility/toi-games/fetch/game/solution?pc=toi&pfm=web&gameId=${gameId}&gameType=${formattedGameType}${tempplayagaincheck}`;
    const requestParams = {
      params: {},
      headers: {
        grxId,
        ssoId,
      },
    };
    setLoader(true);
    setHasError(false);
    makeRequest
      .get(url, requestParams, 'skipfeedengine')
      .then(data => {
        if (data && data.data) {
          const resData = data.data;
          setGameSolutionData(resData);
          const gaData = {
            event: 'user_shown_games',
            type: 'check_solution',
            label: 'solution_popup',
            page_template: 'gameplay',
            section: 'games',
            subsection:
              formattedGameType &&
              formattedGameType !== '' &&
              formattedGameType === 'loop_a_loop'
                ? 'loop_the_loop'
                : formattedGameType,
            browsing_platform: isMobile ? 'mweb' : 'web',
            game_id: gameId,
            level: gameLevel || 'N/A',
            game_of_the_day: window.location.href
              .toLowerCase()
              .includes('/today'),
            login_status: !!ssoId,
            user_grx_id: grxId,
          };
          if (ssoId) {
            gaData.sso_id = ssoId;
          }
          analyticsWrapper('ga4', gaData);
        } else {
          setHasError(true);
        }
        setLoader(false);
      })
      .catch(err => {
        console.log('Game Solution data error:', err);
        setHasError(true);
        setLoader(false);
      });
  };

  useEffect(
    () => {
      if (gameId && gameType) {
        getSolutionData();
      }
    },
    [gameId, gameType],
  );

  // Show loader if external loader is true or internal loader is true
  const isLoading = externalShowLoader || showLoader;

  if (isLoading) {
    return (
      <div className={s.resultContent}>
        <div className={s.loadingState}>
          <LoaderSpinner />
        </div>
      </div>
    );
  }

  // Show error state or no solution state when data is not available
  if (hasError || !gameSolutionData) {
    return (
      <div className={s.resultContent}>
        <div className={s.noSolutionWrap}>
          <div className={s.noSolution}>
            <figure>
              <img
                src={`${TOI_IMAGE_DOMAIN}/photo/113820538.cms`}
                alt="error vector"
              />
            </figure>
            <div className={s.error}>
              <strong>
                {hasError
                  ? 'Failed to load solution. Please try again later.'
                  : 'Solution is not available at the moment'}
              </strong>
              {hasError && (
                <button
                  className={s.retryButton}
                  onClick={() => {
                    setHasError(false);
                    getSolutionData();
                  }}
                >
                  Retry
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const gameComponents = {
    loop_a_loop: LoopALoopGame,
    sudoku: SudukoGame,
    crossword: CrosswordGame,
    mini_crossword: CrosswordGame,
    whos_that: WhoIsThatGame,
    location_guesser: LocationGuesserGame,
    connect: ConnectGame,
  };

  const GameComponent = gameComponents[formattedGameType];

  return (
    <div className={`${s.resultContent} gameSolutionWrap`}>
      {GameComponent && (
        <GameComponent
          gameSolutionData={gameSolutionData}
          viewSolution
          showCongratulationsData
          gamejsonUrl={gamejsonUrl}
          gamesolLevel={gameLevel}
        />
      )}

      {/* Timeline appear after 100ms */}
      {showTimeline && (
        <div className={s.timeLine}>
          {/* Sudoku Game Data */}
          {renderTimeLineItem(
            'Time Taken',
            gameSolutionData?.stateSudoku?.completionTimeInMillis,
            formatTimeMMSS,
            formattedGameType,
          )}
          {renderTimeLineItem(
            'Penalty Time',
            gameSolutionData?.stateSudoku?.penaltyTimeInMillis,
            formatTimeMMSS,
            formattedGameType,
          )}

          {/* Loop A Loop Game Data */}
          {renderTimeLineItem(
            'Time Taken',
            gameSolutionData?.stateLoopALoop?.completionTimeInMillis,
            formatTimeMMSS,
            formattedGameType,
          )}

          {/* Connect Game Data */}
          {renderTimeLineItem(
            'Time Taken',
            gameSolutionData?.stateConnect?.completionTimeInMillis,
            formatTimeMMSS,
            formattedGameType,
          )}
          {renderTimeLineItem(
            'Hint Used',
            gameSolutionData?.stateConnect?.connectData?.connectData?.hintsUsed,
            null,
            formattedGameType,
          )}

          {/* Crossword & Mini Crossword Game Data */}
          {renderTimeLineItem(
            'Time Taken',
            gameSolutionData?.stateCrossword?.completionTimeInMillis,
            formatTimeMMSS,
            formattedGameType,
          )}
          {renderTimeLineItem(
            'Penalty Time',
            gameSolutionData?.stateCrossword?.penaltyTimeInMillis,
            formatTimeMMSS,
            formattedGameType,
          )}

          {/* Location Guesser Game Data */}
          {renderTimeLineItem(
            'Correct Location',
            gameSolutionData?.stateLocationGuesser?.locationName,
            null,
            formattedGameType,
          )}
          {renderTimeLineItem(
            'Best Guess',
            gameSolutionData?.stateLocationGuesser?.bestGuessDistance,
            distance => `${distance} Km Away`,
            formattedGameType,
          )}
          {renderTimeLineItem(
            'Best Score',
            gameSolutionData?.stateLocationGuesser?.bestGuessPoints,
            points => `${points} Points`,
            formattedGameType,
          )}
        </div>
      )}
    </div>
  );
};

GameSolution.propTypes = {
  gameId: PropTypes.string,
  gameType: PropTypes.string,
  showLoader: PropTypes.bool,
};

GameSolution.defaultProps = {
  gameId: '',
  gameType: '',
  showLoader: false,
};

export default GameSolution;
